import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useMarket, fmtDate, expTz, MARKET_LABEL } from "@/lib/market";
import { media } from "@/components/public/Blocks";
import Universe from "@/pages/public/Universe";

export const STATUS_LABEL = { DRAFT: "Brouillon", SCHEDULED: "À venir", LIVE: "En cours", CLOSED: "Terminée", WINNER_PENDING: "Gagnant en validation",
  WINNER_VALIDATED: "Gagnant désigné", PRIZE_DELIVERED: "Lot remis", CANCELLED: "Annulée", ARCHIVED: "Archivée" };

export function ExperienceCard({ e }) {
  const main = e.sponsors?.find((s) => ["presented_by", "main"].includes(s.role));
  return (
    <Link to={`/experience/${e.slug}`} className="p-surface group block overflow-hidden transition-transform duration-300 hover:-translate-y-1" data-testid={`exp-card-${e.slug}`}>
      <div className="p-photo aspect-[16/10]" style={{ backgroundImage: `${e.hero_url ? `url(${media(e.hero_url)})` : "none"}` }}>
        <div className="flex h-full flex-col justify-between p-4">
          <span className={`p-chip self-start ${e.status === "LIVE" ? "p-chip-live" : ""}`}>{STATUS_LABEL[e.status]}</span>
          <p className="font-display text-3xl font-extrabold uppercase leading-none">{e.title || e.name}</p>
        </div>
      </div>
      <div className="space-y-1.5 p-4 text-sm">
        {e.subtitle && <p className="font-semibold p-t2">{e.subtitle}</p>}
        <p className="flex items-center gap-1.5 p-t3"><CalendarDays size={14} /> {fmtDate(e.launch_at, expTz(e.market))} → {fmtDate(e.end_at, expTz(e.market))}</p>
        <p className="flex items-center gap-1.5 p-t3"><MapPin size={14} /> {MARKET_LABEL[e.market]}{e.city ? ` · ${e.city}` : ""}</p>
        {main && <p className="text-xs p-t3">Présentée par <span className="font-bold p-t1">{main.name}</span></p>}
      </div>
    </Link>
  );
}

function Group({ title, rows, testId }) {
  if (!rows.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-5 py-10" data-testid={testId}>
      <h2 className="font-display text-3xl font-extrabold uppercase">{title}</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{rows.map((e) => <ExperienceCard key={e.id} e={e} />)}</div>
    </section>
  );
}

export default function ExperiencesPage() {
  const [market] = useMarket();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/experiences", { params: { market } }).then(({ data: d }) => setData(d)).catch(() => setData({ live: [], upcoming: [], past: [] })); }, [market]);
  const empty = data && !data.live.length && !data.upcoming.length && !data.past.length;
  return (
    <Universe page="experiences"><div id="liste" className="scroll-mt-20" data-testid="experiences-page">
      {data && <>
        <Group title="En cours" rows={data.live} testId="exp-live" />
        <Group title="À venir" rows={data.upcoming} testId="exp-upcoming" />
        <Group title="Terminées" rows={data.past} testId="exp-past" />
      </>}
      {empty && <p className="mx-auto max-w-7xl px-5 py-16 text-base p-t3" data-testid="exp-empty">Aucune Experience n'est ouverte pour le moment. Les prochaines seront annoncées ici et sur nos réseaux.</p>}
    </div></Universe>
  );
}
